import { FormLabelWithOptional } from 'components/form/FormLabelWithOptional';
import { JFormInput } from 'components/form/JFormInput';
import { JFormSelect } from 'components/form/JFormSelect';
import { GridRow } from 'components/GridRow';
import { MFormCheckbox } from 'components/MeridianFinalFormWrappers/MFormCheckbox';
import type { CampaignFormValues } from 'features/campaign/campaign.types';
import { colorValidate } from 'features/traffic/campaigns/content/generic.util';
import { Collapse } from 'react-bootstrap';
import { useField } from 'react-final-form';
import { stylesheet } from 'typestyle';
import { BtfColorPreview } from '../../../BtfColorPreview';
import { BtfTextAlignmentSelect } from '../../../BtfTextAlignmentSelect';
import { BtfTextColorControls } from '../../../BtfTextColorControls';

const css = stylesheet({
  backgroundColor: {
    maxWidth: '120px',
    paddingBottom: '10px',
  },
  backgroundColorPreview: {
    alignSelf: 'center',
    marginTop: '-5px',
  },
  backgroundColorProperties: {
    paddingLeft: '20px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  heading: {
    paddingBottom: '10px',
  },
  rowContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
  },
  textAreaContainer: {
    flexGrow: 7,
  },
});

export function BtfTextProperties({ name }: { name: string }) {
  const { input } = useField(name);
  const {
    input: { value: textBackgroundColor, onChange: onTextColorChange },
  } = useField(`${name}.textProperties.backgroundColor`);

  const textBackgroundColorEnabled = input.value.textProperties.check;

  const handleTextBackgroundColorChange = (value: string) => {
    const prependedValue = `#${value}`;
    if (CSS.supports('color', prependedValue)) {
      onTextColorChange(prependedValue as unknown as CampaignFormValues[keyof CampaignFormValues]);
    }
  };

  const backgroundColorConfig = (
    <div>
      <label>Space around text (padding)</label>
      <div className={css.rowContainer}>
        <JFormInput label="Top (px)" name={`${name}.textProperties.topPadding`} type="number" />
        <JFormInput label="Bottom (px)" name={`${name}.textProperties.bottomPadding`} type="number" />
        <JFormInput label="Left (px)" name={`${name}.textProperties.leftPadding`} type="number" />
        <JFormInput label="Right (px)" name={`${name}.textProperties.rightPadding`} type="number" />
      </div>
      <FormLabelWithOptional label="Rounded corners" optional />
      <GridRow minItemWidth="80px">
        <JFormInput
          label="⌜ Top left (px)"
          min={0}
          name={`${name}.textProperties.borderRadius.topLeft`}
          placeholder="0"
          step={1}
          type="number"
        />
        <JFormInput
          label="⌝ Top right (px)"
          min={0}
          name={`${name}.textProperties.borderRadius.topRight`}
          placeholder="0"
          step={1}
          type="number"
        />
        <JFormInput
          label="⌟ Bottom right (px)"
          min={0}
          name={`${name}.textProperties.borderRadius.bottomRight`}
          placeholder="0"
          step={1}
          type="number"
        />
        <JFormInput
          label="⌞ Bottom left (px)"
          min={0}
          name={`${name}.textProperties.borderRadius.bottomLeft`}
          placeholder="0"
          step={1}
          type="number"
        />
      </GridRow>
    </div>
  );

  return (
    <div className={css.container}>
      <h4 className={css.heading}>Text properties</h4>
      <BtfTextColorControls name={name} />

      <div>
        <label>Header</label>
        <div className={css.rowContainer}>
          <JFormInput label="Header text" name={`${name}.textProperties.headerText`} placeholder="Lorem ipsum header" />
          <BtfTextAlignmentSelect label="Body alignment" name={`${name}.textProperties.headerAlignment`} />
          <JFormSelect
            label="Header level"
            name={`${name}.textProperties.headerLevel`}
            options={[
              {
                text: 'H1',
                value: 'h1',
              },
              {
                text: 'H2',
                value: 'h2',
              },
              {
                text: 'H3',
                value: 'h3',
              },
              {
                text: 'H4',
                value: 'h4',
              },
              {
                text: 'H5',
                value: 'h5',
              },
              {
                text: 'H6',
                value: 'h6',
              },
            ]}
          />
        </div>
      </div>
      <div className={css.rowContainer}>
        <div className={css.textAreaContainer}>
          <JFormInput
            as="textarea"
            description={() => (
              <>
                Markdown supported,{' '}
                <a href="https://w.amazon.com/index.php/ODSE/Features/ODSMarkdownSupport" rel="noopener noreferrer" target="_blank">
                  help
                </a>
              </>
            )}
            label="Text/markdown"
            name={`${name}.textProperties.textMarkdown`}
            placeholder="Lorem ipsum body text with **bold** text!
[Learn more about Echo](https://www.amazon.com)"
          />
        </div>
        <BtfTextAlignmentSelect label="Body alignment" name={`${name}.textProperties.bodyAlignment`} />
      </div>
      <MFormCheckbox
        label="Add background color (enables padding, content max width, and rounded corners)"
        name={`${name}.textProperties.check`}
      />
      <Collapse in={!!textBackgroundColorEnabled}>
        <div className={css.backgroundColorProperties}>
          <div className={css.rowContainer}>
            <JFormInput
              className={css.backgroundColor}
              label="Background Color"
              name={`${name}.textProperties.backgroundColor`}
              onChange={handleTextBackgroundColorChange}
              placeholder="#111111"
              validate={(value: string) => colorValidate(value, !!textBackgroundColorEnabled)}
            />
            <div className={css.backgroundColorPreview}>
              <BtfColorPreview color={textBackgroundColor} />
            </div>
          </div>
          {backgroundColorConfig}
        </div>
      </Collapse>
    </div>
  );
}
